import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import api, { imgUrl } from '../api';
import { useAuth } from '../components/AuthContext';
import { CrystalBallIcon, ChatIcon, FreeIcon, StarIcon, TarotIcon, PalmIcon, HeartIcon, BriefcaseIcon, HomeIcon, CalendarIcon, HashIcon, ShieldIcon } from '../components/Icons';

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.15 } },
};

function Section({ children, className = '', dark = true }) {
  return (
    <section className={`min-h-screen flex items-center justify-center px-6 ${dark ? 'bg-luna-dark' : 'bg-[#f5f5f7] text-gray-900'} ${className}`}>
      <div className="max-w-5xl w-full py-24">{children}</div>
    </section>
  );
}

function CopyButton() {
  const [copied, setCopied] = useState(false);
  return (
    <motion.button
      variants={fadeUp}
      onClick={() => { navigator.clipboard.writeText('3333154167499'); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      className="mt-4 px-6 py-2 rounded-full bg-gradient-to-r from-luna-purple to-luna-pink text-white text-sm hover:opacity-90 transition"
    >
      {copied ? '✓ 복사 완료!' : '계좌번호 복사'}
    </motion.button>
  );
}

export default function LandingPage() {
  const { socket } = useAuth();
  const [counselors, setCounselors] = useState([]);
  const [counselorError, setCounselorError] = useState(false);

  const fetchCounselors = () => {
    setCounselorError(false);
    api.counselors()
      .then(d => {
        const all = d.counselors || [];
        const order = { online: 0, busy: 1, offline: 2 };
        all.sort((a, b) => (order[a.status] ?? 2) - (order[b.status] ?? 2));
        setCounselors(all.slice(0, 15));
      })
      .catch(err => { console.error('[Landing] counselors fetch error:', err); setCounselorError(true); });
  };

  useEffect(() => { fetchCounselors(); }, []);

  // Real-time LIVE status updates
  useEffect(() => {
    if (!socket) return;
    const handler = ({ counselor_id, is_live }) => {
      setCounselors(prev => prev.map(c => c.id === counselor_id ? { ...c, is_live } : c));
    };
    socket.on('counselor_live_changed', handler);
    return () => { socket.off('counselor_live_changed', handler); };
  }, [socket]);

  return (
    <div className="overflow-hidden">
      <Helmet>
        <title>루나문 LunaMoon — 사주·타로·신점 상담 매칭 플랫폼</title>
        <meta name="description" content="검증된 전문 상담사와 실시간 채팅·음성통화로 사주, 타로, 신점, 궁합, 운세 상담을 받아보세요. 무료 가입, 빠른 매칭." />
        <link rel="canonical" href="https://lunamoon.io" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: '루나문 LunaMoon',
          url: 'https://lunamoon.io',
          description: '사주·타로·신점 상담 매칭 플랫폼',
          potentialAction: { '@type': 'SearchAction', target: 'https://lunamoon.io/counselors?search={search_term_string}', 'query-input': 'required name=search_term_string' }
        })}</script>
      </Helmet>
      {/* ─── Hero ─── */}
      <section className="relative flex items-center justify-center text-center px-4 sm:px-6 bg-luna-dark overflow-hidden" style={{ minHeight: '100vh' }}>
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/30 via-transparent to-luna-dark" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-luna-purple/15 rounded-full blur-[140px]" />
        <div className="absolute top-1/4 left-1/4 w-48 h-48 bg-pink-500/10 rounded-full blur-[80px]" />
        <div className="absolute top-1/3 right-1/4 w-48 h-48 bg-violet-500/10 rounded-full blur-[80px]" />
        <motion.div
          initial="hidden" animate="visible" variants={stagger}
          className="relative z-10 max-w-3xl"
        >
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-white/[0.06] backdrop-blur-sm border border-white/[0.10] rounded-full px-4 py-1.5 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-luna-violet animate-pulse" />
            <span className="text-[12px] text-white/60 tracking-widest uppercase font-medium">사주 · 타로 · 신점 상담</span>
          </motion.div>
          <motion.h1 variants={fadeUp} style={{ fontSize: 'clamp(3rem,8vw,5.5rem)', fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.04em', marginBottom: 24 }}>
            당신의 운명을<br />
            <span className="bg-gradient-to-r from-luna-violet via-luna-pink to-luna-gold bg-clip-text text-transparent">
              밝혀줄 상담사
            </span>를<br />
            만나보세요
          </motion.h1>
          <motion.p variants={fadeUp} className="text-white/50 mb-10 leading-relaxed px-2" style={{ fontSize: 'clamp(1rem,2.5vw,1.2rem)' }}>
            검증된 전문 상담사와 실시간 채팅 · 음성 통화로<br className="hidden md:block" />
            편하게 상담받으세요. 최적의 상담사를 빠르게 찾아드립니다.
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
            <Link to="/counselors" className="w-full sm:w-auto px-8 py-4 rounded-full bg-luna-purple text-white font-semibold hover:bg-luna-purple/80 transition text-base sm:text-lg text-center">
              상담사 둘러보기
            </Link>
            <Link to="/register" className="w-full sm:w-auto px-8 py-4 rounded-full border border-white/20 text-white font-semibold hover:bg-white/10 transition text-base sm:text-lg text-center">
              무료 시작하기
            </Link>
          </motion.div>
          {/* Stats */}
          <motion.div variants={fadeUp} className="mt-16 flex flex-wrap items-center justify-center gap-8 md:gap-16">
            {[
              { num: '500+', label: '검증 상담사' },
              { num: '실시간', label: '채팅·음성 상담' },
              { num: '4.8★', label: '평균 만족도' },
              { num: '24시간', label: '상담 가능' },
            ].map(s => (
              <div key={s.label} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white tracking-tight">{s.num}</div>
                <div className="text-[11px] text-white/30 mt-0.5 uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 1 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/20 rounded-full flex justify-center pt-2">
            <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.5 }} className="w-1.5 h-1.5 bg-white/50 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* ─── Featured Counselors ─── */}
      {(counselors.length > 0 || counselorError) && (
        <Section>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={stagger} className="text-center">
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              지금 활동 중인 <span className="bg-gradient-to-r from-luna-violet to-luna-pink bg-clip-text text-transparent">상담사</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-white/50 text-lg mb-12">
              검증된 전문 상담사를 만나보세요
            </motion.p>
            {counselorError && counselors.length === 0 ? (
              <motion.div variants={fadeUp} className="py-12">
                <p className="text-white/40 mb-4">상담사 목록을 불러오지 못했습니다.</p>
                <button onClick={fetchCounselors} className="px-6 py-2.5 rounded-full bg-luna-purple text-white text-sm hover:bg-luna-purple/80 transition">
                  다시 시도
                </button>
              </motion.div>
            ) : (
            <motion.div variants={stagger} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-5 mb-10">
              {counselors.map((c) => (
                <motion.div key={c.id} variants={fadeUp}>
                  <Link to={`/counselors/${c.id}`}
                    className="block rounded-2xl p-[1px] hover:scale-[1.03] transition-transform duration-300"
                    style={{ background: 'linear-gradient(135deg, rgba(167,139,250,0.2), rgba(244,114,182,0.12), rgba(124,58,237,0.08))' }}>
                    <div className="rounded-2xl p-4 sm:p-5 h-full"
                      style={{
                        background: 'linear-gradient(135deg, rgba(15,15,35,0.9), rgba(20,10,40,0.95))',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                      }}>
                      <div className="flex flex-col items-center text-center">
                        <div className="relative mb-3">
                          {c.is_live && (
                            <span className="absolute -top-1.5 -right-1.5 z-10 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold animate-pulse shadow-lg shadow-red-500/40">LIVE</span>
                          )}
                          {c.profile_photo_url ? (
                            <>
                            <img src={imgUrl(c.profile_photo_url)} alt="" className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover ring-1 ring-white/10"
                              onError={e => { e.target.onerror = null; e.target.style.display = 'none'; e.target.nextElementSibling && (e.target.nextElementSibling.style.display = 'flex'); }}
                            />
                            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-luna-purple/60 to-luna-pink/40 items-center justify-center text-xl sm:text-2xl font-bold ring-1 ring-white/10" style={{display:'none'}}>
                              {(c.display_name || '?')[0]}
                            </div>
                            </>
                          ) : (
                            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-luna-purple/60 to-luna-pink/40 flex items-center justify-center text-xl sm:text-2xl font-bold ring-1 ring-white/10">
                              {(c.display_name || '?')[0]}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-sm sm:text-base">{c.display_name}</h3>
                          <span className={`w-2 h-2 rounded-full shrink-0 ${c.status === 'online' ? 'bg-green-400' : c.status === 'busy' ? 'bg-yellow-400' : 'bg-white/30'}`} />
                        </div>
                        {c.specialties?.length > 0 && (
                          <div className="flex flex-wrap justify-center gap-1 mb-2">
                            {c.specialties.slice(0, 3).map(s => (
                              <span key={s} className="text-[10px] sm:text-xs px-2 py-0.5 rounded-full bg-luna-purple/15 text-luna-violet/80">{s}</span>
                            ))}
                          </div>
                        )}
                        {c.rating_avg > 0 && (
                          <div className="text-xs text-luna-gold">★ {c.rating_avg} <span className="text-white/30">({c.review_count})</span></div>
                        )}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
            )}
            <motion.div variants={fadeUp}>
              <Link to="/counselors" className="inline-block px-8 py-3 rounded-full border border-white/20 text-white font-medium hover:bg-white/10 transition">
                전체 상담사 보기 →
              </Link>
            </motion.div>
          </motion.div>
        </Section>
      )}

      {/* ─── Highlights ─── */}
      <Section dark={false}>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={stagger} className="text-center">
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            왜 <span className="text-luna-purple">루나문</span>인가요?
          </motion.h2>
          <motion.p variants={fadeUp} className="text-gray-500 text-lg mb-16">
            다른 플랫폼과는 차원이 다릅니다
          </motion.p>
          <motion.div variants={stagger} className="grid md:grid-cols-3 gap-8">
            {[
              { Icon: ShieldIcon, title: '검증된 상담사', desc: '전문 자격과 경력을 검증한 상담사만 활동합니다.' },
              { Icon: ChatIcon, title: '실시간 소통', desc: '채팅과 음성통화로 바로 연결. 기다림 없이 상담하세요.' },
              { Icon: FreeIcon, title: '간편한 매칭', desc: '복잡한 과정 없이 원하는 상담사와 바로 연결됩니다.' },
            ].map((item) => (
              <motion.div key={item.title} variants={fadeUp} className="bg-white/75 backdrop-blur-xl rounded-3xl p-8 shadow-[0_8px_32px_rgba(120,60,200,0.08)] border border-white/60 hover:shadow-[0_12px_40px_rgba(120,60,200,0.14)] hover:-translate-y-1 transition-all duration-300">
                <div className="mb-6 text-luna-purple"><item.Icon size={48} /></div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{item.title}</h3>
                <p className="text-gray-500 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </Section>

      {/* ─── How it works ─── */}
      <Section>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={stagger} className="text-center">
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            이용 방법
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/50 text-lg mb-16">
            3단계로 간편하게 시작하세요
          </motion.p>
          <motion.div variants={stagger} className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: '회원가입', desc: '이메일로 간편하게 가입하세요.' },
              { step: '02', title: '상담사 선택', desc: '전문 분야, 리뷰를 확인하고 선택하세요.' },
              { step: '03', title: '상담 시작', desc: '채팅 또는 음성통화로 바로 연결됩니다.' },
            ].map((item) => (
              <motion.div key={item.step} variants={fadeUp} className="relative">
                <div className="text-7xl font-black text-luna-purple/20 mb-2">{item.step}</div>
                <h3 className="text-2xl font-bold mb-3">{item.title}</h3>
                <p className="text-white/50 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </Section>

      {/* ─── Specialties ─── */}
      <Section dark={false}>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={stagger} className="text-center">
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            전문 상담 분야
          </motion.h2>
          <motion.p variants={fadeUp} className="text-gray-500 text-lg mb-16">
            다양한 분야의 전문 상담사가 기다리고 있습니다
          </motion.p>
          <motion.div variants={stagger} className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { Icon: StarIcon, label: '사주팔자' },
              { Icon: TarotIcon, label: '타로카드' },
              { Icon: PalmIcon, label: '손금/관상' },
              { Icon: HeartIcon, label: '연애/궁합' },
              { Icon: BriefcaseIcon, label: '취업/진로' },
              { Icon: HomeIcon, label: '이사/풍수' },
              { Icon: CalendarIcon, label: '택일' },
              { Icon: HashIcon, label: '수비학' },
            ].map((item) => (
              <motion.div key={item.label} variants={fadeUp} className="bg-white/75 backdrop-blur-xl rounded-2xl p-6 shadow border border-white/60 hover:shadow-[0_8px_24px_rgba(120,60,200,0.12)] hover:-translate-y-1.5 transition-all duration-300 cursor-pointer">
                <div className="mb-3 text-luna-purple"><item.Icon size={40} /></div>
                <div className="font-semibold">{item.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </Section>

      {/* ─── Testimonials ─── */}
      <Section>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={stagger} className="text-center">
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            이용 후기
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/50 text-lg mb-16">
            실제 사용자의 생생한 후기
          </motion.p>
          <motion.div variants={stagger} className="grid md:grid-cols-3 gap-8">
            {[
              { name: '김** 님', stars: 5, text: '처음 사주를 봤는데 너무 정확해서 소름돋았어요. 상담사분도 친절하시고 꼼꼼하게 설명해주셨습니다.' },
              { name: '이** 님', stars: 5, text: '타로 상담 받았는데 고민이 정리됐어요. 채팅으로 편하게 할 수 있어서 좋았습니다.' },
              { name: '박** 님', stars: 4, text: '간편하게 상담사를 찾을 수 있어서 좋았어요. 상담 퀄리티도 높아서 주변에 추천하고 있어요.' },
            ].map((item) => (
              <motion.div key={item.name} variants={fadeUp} className="bg-white/5 border border-white/10 rounded-3xl p-8 text-left">
                <div className="text-luna-gold text-lg mb-4">{'★'.repeat(item.stars)}{'☆'.repeat(5 - item.stars)}</div>
                <p className="text-white/80 leading-relaxed mb-6">"{item.text}"</p>
                <p className="text-white/40 text-sm font-medium">{item.name}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </Section>

      {/* ─── CTA Footer ─── */}
      <section className="relative min-h-[60vh] flex items-center justify-center text-center px-6 bg-luna-dark overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-luna-purple/20 via-transparent to-transparent" />
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="relative z-10">
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-6xl font-bold mb-6">
            지금 바로 시작하세요
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/50 text-lg mb-10">
            당신의 운명이 기다리고 있습니다
          </motion.p>
          <motion.div variants={fadeUp}>
            <Link to="/register" className="px-10 py-5 rounded-full bg-gradient-to-r from-luna-purple to-luna-pink text-white font-bold text-xl hover:opacity-90 transition inline-block">
              회원가입
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* ─── 개발자 응원하기 ─── */}
      <section className="bg-luna-dark/80 py-16 px-6 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-md mx-auto bg-white/[0.07] backdrop-blur-xl border border-white/[0.15] rounded-2xl p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.1)]">
          <motion.div variants={fadeUp} className="text-4xl mb-4">☕</motion.div>
          <motion.h3 variants={fadeUp} className="text-xl font-bold text-white mb-3">개발자에게 커피 사기</motion.h3>
          <motion.p variants={fadeUp} className="text-white/50 text-sm mb-6 leading-relaxed">
            루나문은 더 나은 서비스를 위해 끊임없이 발전하고 있습니다.<br/>
            커피 한 잔의 응원이 사이트 발전에 재투자됩니다 💜
          </motion.p>
          <motion.div variants={fadeUp} className="bg-white/[0.08] backdrop-blur-lg rounded-xl p-4 space-y-2 border border-white/[0.12] shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]">
            <div className="text-white/70 text-sm">카카오뱅크</div>
            <div className="text-white font-mono text-lg font-bold tracking-wider">3333-15-4167499</div>
            <div className="text-white/50 text-sm">박성모</div>
          </motion.div>
          <CopyButton />
        </motion.div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="relative bg-[rgba(6,2,16,0.85)] backdrop-blur-[32px] backdrop-saturate-[180%] border-t border-purple-500/[0.15] py-12 px-6 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-purple-400/25 to-transparent pointer-events-none" aria-hidden />
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-white/40 text-sm">
          <div>© 2026 루나문 LunaMoon. All rights reserved.</div>
          <div className="flex gap-6">
            <Link to="/terms" className="hover:text-white/60 transition">이용약관</Link>
            <Link to="/privacy" className="hover:text-white/60 transition">개인정보처리방침</Link>
            <Link to="/support" className="hover:text-white/60 transition">고객센터</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
